import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ListTodo,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import TaskOriginBadge from './TaskOriginBadge';
import TaskScoreModal from './TaskScoreModal';
import {
  isTaskOverdue,
  taskTypeLabel,
  type NetworkingTaskRow,
} from '../../lib/networkingTasks';

const LIST_LIMIT = 20;

function formatDue(dueAt: string): string {
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return dueAt;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function contactLabel(task: NetworkingTaskRow): string {
  const sub = task.subscriber;
  const name = sub?.name?.trim();
  const email = sub?.email?.trim() || task.contact_email;
  if (name) return name;
  return email || '—';
}

interface PendingTasksDashboardPanelProps {
  /** Bump to refetch (e.g. parent dashboard refresh). */
  refreshKey?: number;
}

export default function PendingTasksDashboardPanel({ refreshKey = 0 }: PendingTasksDashboardPanelProps) {
  const [tasks, setTasks] = useState<NetworkingTaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [closingTask, setClosingTask] = useState<NetworkingTaskRow | null>(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('*, subscriber:subscribers(id, name, email), transcript:transcripts(id, title, meeting_date)')
        .in('status', ['pending', 'in_progress'])
        .order('due_at', { ascending: true })
        .limit(LIST_LIMIT);
      if (error) throw error;
      setTasks((data ?? []) as NetworkingTaskRow[]);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTasks();
  }, [fetchTasks, refreshKey]);

  const overdueCount = useMemo(
    () => tasks.filter((t) => isTaskOverdue(t.due_at, t.status)).length,
    [tasks],
  );

  return (
    <aside className="xl:sticky xl:top-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden flex flex-col max-h-[calc(100vh-8rem)]">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 shrink-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <ListTodo className="w-4 h-4 text-amber-500" />
              Pending tasks
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Networking follow-ups · pre-screen, enrich, LinkedIn
            </p>
          </div>
          <button
            type="button"
            onClick={() => void fetchTasks()}
            disabled={loading}
            className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
            title="Refresh tasks"
            aria-label="Refresh tasks"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {!loading && tasks.length > 0 ? (
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
            <span className="font-semibold text-gray-900 dark:text-white">{tasks.length}</span>
            {tasks.length >= LIST_LIMIT ? '+' : ''} open
            {overdueCount > 0 ? (
              <span className="text-amber-700 dark:text-amber-300 font-medium">
                {' '}
                · {overdueCount} overdue
              </span>
            ) : null}
          </p>
        ) : null}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-10 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : tasks.length === 0 ? (
          <p className="px-4 py-8 text-sm text-center text-gray-500 dark:text-gray-400">
            <CheckCircle2 className="w-7 h-7 mx-auto mb-2 text-emerald-500 opacity-80" />
            No pending tasks.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {tasks.map((task) => {
              const overdue = isTaskOverdue(task.due_at, task.status);
              const subscriberHref = task.subscriber_id
                ? `/subscribers/${task.subscriber_id}?tab=tasks`
                : null;
              const transcriptHref = task.transcript_id ? `/transcripts/${task.transcript_id}` : null;

              return (
                <li key={task.id} className="px-4 py-3 hover:bg-gray-50/80 dark:hover:bg-gray-900/30">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2">
                        {task.title || taskTypeLabel(task.task_type)}
                      </p>
                      {transcriptHref ? (
                        <Link
                          to={transcriptHref}
                          className="text-xs text-teal-600 dark:text-teal-400 hover:underline truncate block mt-0.5"
                        >
                          {task.transcript?.title?.trim() || 'Open transcript'}
                        </Link>
                      ) : subscriberHref ? (
                        <Link
                          to={subscriberHref}
                          className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline truncate block mt-0.5"
                        >
                          {contactLabel(task)}
                        </Link>
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {contactLabel(task)}
                        </p>
                      )}
                      <p
                        className={`text-xs mt-1 ${overdue ? 'text-amber-700 dark:text-amber-300 font-medium' : 'text-gray-500 dark:text-gray-400'}`}
                      >
                        Due {formatDue(task.due_at)}
                        {overdue ? ' · overdue' : ''}
                      </p>
                      <div className="mt-1.5">
                        <TaskOriginBadge task={task} compact />
                      </div>
                    </div>
                    <button
                      type="button"
                      title="Close out — record value generated"
                      onClick={() => setClosingTask(task)}
                      className="shrink-0 p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 shrink-0">
        <Link
          to="/tasks"
          className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
        >
          Open full task queue
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {closingTask ? (
        <TaskScoreModal
          isOpen
          task={closingTask}
          intent="complete"
          onClose={() => setClosingTask(null)}
          onSaved={() => void fetchTasks()}
        />
      ) : null}
    </aside>
  );
}
